/**
 * Session variety metrics — v1.
 * Pure, read-only. No side effects. Computes metrics from existing session state.
 *
 * Exports:
 *   computeSessionSaturationScore  — one session-level metric ∈ [0, 1]
 *   marginalVarietyGain            — per-player ranking signal (higher = more variety)
 *   marginalVarietyGainForUnit     — same for a unit (single or partnership)
 *
 * v2 deferred: ClusteringScore, Gini, community detection, variety debt, bridge centrality.
 */

/**
 * Build a Set of sorted partnership pair keys to exclude from pair calculations.
 * @param {Array} partnerships
 * @returns {Set<string>}
 */
function buildPartnershipPairKeys(partnerships) {
  const set = new Set();
  if (!Array.isArray(partnerships)) return set;
  for (const p of partnerships) {
    const a = p.player1Id || p.player1_id;
    const b = p.player2Id || p.player2_id;
    if (a && b) set.add([a, b].sort().join('-'));
  }
  return set;
}

/**
 * Get co-appearance count for a pair from playerInteractions map.
 * @param {Object} playerInteractions - { "sortedId1-sortedId2": count }
 * @param {string} idA
 * @param {string} idB
 * @returns {number}
 */
function getPairCount(playerInteractions, idA, idB) {
  if (!playerInteractions) return 0;
  return playerInteractions[[idA, idB].sort().join('-')] || 0;
}

/**
 * Normalize a player entry from matchHistory (string or { id: string }).
 * @param {*} entry
 * @returns {string|null}
 */
function normalizeId(entry) {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object' && entry.id) return String(entry.id);
  return null;
}

/**
 * Extract the sorted 4-player quartet key from a match history entry.
 * Returns null if the entry does not represent a 4-player doubles game.
 * @param {Object} game
 * @returns {string[]|null} sorted player IDs, or null
 */
function extractGameQuartet(game) {
  let t1 = [];
  let t2 = [];
  if (Array.isArray(game.team1) && Array.isArray(game.team2)) {
    t1 = game.team1;
    t2 = game.team2;
  } else if (Array.isArray(game.teams) && game.teams.length >= 2) {
    [t1, t2] = game.teams;
  }
  const ids = [...t1, ...t2].map(normalizeId).filter(Boolean);
  if (ids.length !== 4) return null;
  return ids.sort();
}

// ─── Component A: pairSaturation ──────────────────────────────────────────────

/**
 * Fraction of the top-K non-partnership pairs (by interaction count) with count ≥ 2.
 * K = min(10, total non-partnership pair count).
 *
 * @param {string[]} activePlayerIds
 * @param {Object} playerInteractions
 * @param {Set<string>} partnershipPairKeys
 * @returns {number} ∈ [0, 1]
 */
function computePairSaturation(activePlayerIds, playerInteractions, partnershipPairKeys) {
  if (!activePlayerIds || activePlayerIds.length < 2) return 0;

  const counts = [];
  for (let i = 0; i < activePlayerIds.length; i++) {
    for (let j = i + 1; j < activePlayerIds.length; j++) {
      const a = activePlayerIds[i];
      const b = activePlayerIds[j];
      if (partnershipPairKeys.has([a, b].sort().join('-'))) continue;
      counts.push(getPairCount(playerInteractions, a, b));
    }
  }
  if (counts.length === 0) return 0;

  counts.sort((x, y) => y - x);
  const K = Math.min(10, counts.length);
  const saturated = counts.slice(0, K).filter(c => c >= 2).length;
  return saturated / K;
}

// ─── Component B: repeatQuartetRate ───────────────────────────────────────────

/**
 * Fraction of 4-player games in matchHistory where those exact 4 players
 * appeared together in a prior game.
 *
 * @param {Array} matchHistory
 * @returns {number} ∈ [0, 1]
 */
function computeRepeatQuartetRate(matchHistory) {
  if (!Array.isArray(matchHistory) || matchHistory.length < 2) return 0;

  const seen = new Set();
  let repeatCount = 0;
  let validGames = 0;

  for (const game of matchHistory) {
    const quartet = extractGameQuartet(game);
    if (!quartet) continue;
    validGames++;
    const key = quartet.join(',');
    if (seen.has(key)) {
      repeatCount++;
    } else {
      seen.add(key);
    }
  }
  return validGames === 0 ? 0 : repeatCount / validGames;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Compute sessionSaturationScore ∈ [0, 1].
 * Higher = more saturated / stuck in a rotation trap.
 *
 * Components:
 *   pairSaturation    (weight 0.6) — fraction of top-K pairs already over-exposed
 *   repeatQuartetRate (weight 0.4) — fraction of games where same 4 played together before
 *
 * @param {Object} sessionState       - { playerInteractions, matchHistory }
 * @param {string[]} activePlayerIds  - players currently in the session
 * @param {Array}   [partnerships=[]] - to exempt partner pairs from pair saturation
 * @param {Object}  [config={}]       - { pairSaturationWeight, repeatQuartetWeight }
 * @returns {{ score: number, pairSaturation: number, repeatQuartetRate: number }}
 */
export function computeSessionSaturationScore(sessionState, activePlayerIds, partnerships = [], config = {}) {
  const {
    pairSaturationWeight = 0.6,
    repeatQuartetWeight = 0.4,
  } = config;

  const playerInteractions = sessionState?.playerInteractions || {};
  const matchHistory = sessionState?.matchHistory || [];
  const partnershipPairKeys = buildPartnershipPairKeys(partnerships);

  const pairSaturation = computePairSaturation(activePlayerIds, playerInteractions, partnershipPairKeys);
  const repeatQuartetRate = computeRepeatQuartetRate(matchHistory);
  const score = Math.min(1, Math.max(0,
    pairSaturationWeight * pairSaturation + repeatQuartetWeight * repeatQuartetRate,
  ));

  return { score, pairSaturation, repeatQuartetRate };
}

/**
 * Compute marginalVarietyGain for a single player added to an in-progress shaped pool.
 * Higher = adding this player improves session variety.
 * Pure ranking signal — never used for exclusion.
 *
 * Formula: w1 * pairNovelty + w2 * quartetNovelty − w3 * exposurePenalty
 *
 * pairNovelty   — how fresh is this player relative to current pool members
 *                 (1.0 = never played together, 0.5 = played once, 0.0 = ≥2 times)
 * quartetNovelty — 1.0 if the resulting quartet has never played together (only when |pool| == 3)
 * exposurePenalty — how often does this player max-repeat with any one opponent
 *
 * @param {string}   playerId
 * @param {string[]} currentPoolIds      - player IDs already in the shaped pool
 * @param {string[]} allSessionPlayerIds - all active players (for exposurePenalty)
 * @param {Object}   sessionState        - { playerInteractions, gamesPlayedThisSession, matchHistory }
 * @param {Array}    [partnerships=[]]
 * @param {Object}   [weights={}]        - { w1, w2, w3 }
 * @returns {number}
 */
export function marginalVarietyGain(playerId, currentPoolIds, allSessionPlayerIds, sessionState, partnerships = [], weights = {}) {
  const { w1 = 0.5, w2 = 0.3, w3 = 0.2 } = weights;

  const playerInteractions = sessionState?.playerInteractions || {};
  const gamesPlayedThisSession = sessionState?.gamesPlayedThisSession || {};
  const matchHistory = sessionState?.matchHistory || [];
  const partnershipPairKeys = buildPartnershipPairKeys(partnerships);

  const getCount = (a, b) => {
    const key = [a, b].sort().join('-');
    return partnershipPairKeys.has(key) ? 0 : (playerInteractions[key] || 0);
  };

  const S = currentPoolIds;

  // 1. Pair novelty — mean over current pool members
  let pairNovelty = 1.0;
  if (S.length > 0) {
    const values = S.map(q => {
      const c = getCount(playerId, q);
      if (c === 0) return 1.0;
      if (c === 1) return 0.5;
      return 0.0;
    });
    pairNovelty = values.reduce((sum, v) => sum + v, 0) / values.length;
  }

  // 2. Quartet novelty — only meaningful when pool already has 3 players
  let quartetNovelty = 0.5; // neutral when not applicable
  if (S.length === 3) {
    const quartetKey = [...S, playerId].sort().join(',');
    const appeared = matchHistory.some(game => {
      const q = extractGameQuartet(game);
      return q !== null && q.join(',') === quartetKey;
    });
    quartetNovelty = appeared ? 0.0 : 1.0;
  }

  // 3. Exposure penalty — fraction of games this player spent with their most-seen opponent
  const gamesPlayed = gamesPlayedThisSession[playerId] || 0;
  let exposurePenalty = 0;
  if (gamesPlayed > 0 && allSessionPlayerIds.length > 0) {
    const maxCount = allSessionPlayerIds
      .filter(q => q !== playerId)
      .reduce((max, q) => Math.max(max, getCount(playerId, q)), 0);
    exposurePenalty = Math.min(1, maxCount / gamesPlayed);
  }

  return w1 * pairNovelty + w2 * quartetNovelty - w3 * exposurePenalty;
}

/**
 * Compute marginalVarietyGain for a unit (1 or 2 players).
 * For partnerships, returns the conservative (min) gain across both players.
 *
 * @param {string[]} unitPlayerIds
 * @param {string[]} currentPoolIds
 * @param {string[]} allSessionPlayerIds
 * @param {Object}   sessionState
 * @param {Array}    [partnerships=[]]
 * @param {Object}   [weights={}]
 * @returns {number}
 */
export function marginalVarietyGainForUnit(unitPlayerIds, currentPoolIds, allSessionPlayerIds, sessionState, partnerships = [], weights = {}) {
  if (!unitPlayerIds || unitPlayerIds.length === 0) return 0;
  if (unitPlayerIds.length === 1) {
    return marginalVarietyGain(unitPlayerIds[0], currentPoolIds, allSessionPlayerIds, sessionState, partnerships, weights);
  }
  // Partnership: conservative min so a saturated partner doesn't pull in a novel one
  const gains = unitPlayerIds.map(id =>
    marginalVarietyGain(id, currentPoolIds, allSessionPlayerIds, sessionState, partnerships, weights),
  );
  return Math.min(...gains);
}
